import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout, Button } from '../common/index.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import { Truck, TrendingUp, Package, ArrowLeft } from 'lucide-react';

const VehicleAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filter === 'custom' ? { filter, ...customDates } : { filter };
      const response = await analyticsService.getVehicleAnalytics(params);
      setData(response);
    } catch (error) {
      console.error('Error fetching vehicle analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const VehicleCard = ({ vehicle, isTopPerformer }) => (
    <div className={`bg-white rounded-card shadow-card p-4 lg:p-6 hover:shadow-lg transition-shadow ${
      isTopPerformer ? 'border-2 border-yellow-400' : ''
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="p-3 bg-blue-100 rounded-full mr-3">
            <Truck className="w-6 h-6 text-brand" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{vehicle.vehicle_number}</h3>
            <p className="text-sm text-gray-600">{vehicle.vehicle_name}</p>
            <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
              vehicle.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {vehicle.status}
            </span>
          </div>
        </div>
        {isTopPerformer && (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
            Top Performer
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-600">Shipments</p>
          <p className="text-lg font-bold text-gray-800">{vehicle.performance.total_shipments}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Revenue</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(vehicle.performance.total_revenue)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Avg Revenue</p>
          <p className="text-lg font-bold text-brand">{formatCurrency(vehicle.performance.average_revenue_per_shipment)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Balance</p>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(vehicle.performance.balance_amount)}</p>
        </div>
      </div>

      {/* Status Breakdown */}
      {Object.keys(vehicle.shipment_status_breakdown).length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <p className="text-xs text-gray-600 mb-2">Shipment Status</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(vehicle.shipment_status_breakdown).map(([status, count]) => (
              <span key={status} className="px-2 py-1 bg-gray-100 text-xs rounded-full">
                {status}: {count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-card shadow-card p-4 lg:p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-xl lg:text-2xl font-bold text-gray-800">{value}</h3>
          {subtitle && <p className="text-xs lg:text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 lg:p-3 rounded-full ${color}`}>
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageLayout title="Vehicle Analytics" icon={Truck}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-secondary">Loading vehicle analytics...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Vehicle Analytics"
      icon={Truck}
      actions={
        <>
          <DateFilterDropdown filter={filter} setFilter={setFilter} customDates={customDates} setCustomDates={setCustomDates} onApply={fetchData} dateRange={data?.filter} />
          <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/analytics')}>Back</Button>
        </>
      }
    >
      <p className="text-sm text-secondary -mt-2 mb-6">Fleet performance and utilization</p>

                {/* Summary Stats */}
                {data && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        title="Total Vehicles"
                        value={data.summary.total_vehicles}
                        subtitle="In fleet"
                        icon={Truck}
                        color="bg-brand"
                      />
                      <StatCard
                        title="Active Vehicles"
                        value={data.summary.active_vehicles}
                        subtitle="Currently active"
                        icon={TrendingUp}
                        color="bg-green-500"
                      />
                      <StatCard
                        title="Vehicles with Shipments"
                        value={data.summary.vehicles_with_shipments}
                        subtitle="In this period"
                        icon={Package}
                        color="bg-purple-500"
                      />
                      <StatCard
                        title="Unassigned Shipments"
                        value={data.summary.unassigned_shipments}
                        subtitle="Need assignment"
                        icon={Package}
                        color="bg-blue-600"
                      />
                    </div>

                    {/* Top Performers */}
                    {data.top_performers && data.top_performers.length > 0 && (
                      <div className="mb-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Top Performing Vehicles</h2>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {data.top_performers.slice(0, 3).map((vehicle) => (
                            <VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} isTopPerformer={true} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* All Vehicles */}
                    <div>
                      <h2 className="text-xl font-bold text-gray-800 mb-4">All Vehicles</h2>
                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {data.vehicles.map((vehicle) => (
                          <VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} isTopPerformer={false} />
                        ))}
                      </div>
                    </div>

                    {data.vehicles.length === 0 && (
                      <div className="text-center py-12 bg-gray-50 rounded-card">
                        <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No vehicle data available for the selected period</p>
                      </div>
                    )}
                  </>
                )}
    </PageLayout>
  );
};

export default VehicleAnalytics;